import type { Campaign, CreateCampaignRequest, UpdateCampaignRequest } from '@/types/campaigns';
import type { CampaignAlcance } from './campaigns-format';

/**
 * Formulario de una fila de gasto: estado de la pantalla y conversión al contrato.
 *
 * Igual que en Productos y Órdenes, la conversión vive separada de los componentes para poder
 * probarla sin navegador (`smoke-campaigns.tsx`). Tres detalles del contrato mandan acá:
 *  - `metaAdId` / `metaCampaignId` son `nullable`: al CREAR se manda `null` explícito en el que no
 *    aplica (así «gasto de la campaña» no puede quedar como gasto de un anuncio por descuido) y al
 *    EDITAR también, porque `undefined` significa «no toques este campo» y vaciarlo sería imposible;
 *  - `gastoActual` es un número ≥ 0: un input vacío en el ALTA no se manda (el backend pone 0) y en
 *    la EDICIÓN significa 0 (un Decimal no se puede «vaciar» a null);
 *  - `moneda` se normaliza a mayúsculas (el schema hace `.transform(toUpperCase)`).
 */

/** Estado del formulario de una fila (los importes como texto: el input puede estar vacío). */
export interface CampaignFormState {
  nombre: string;
  /** ¿El gasto es de un anuncio concreto (necesita `metaAdId`) o de la campaña entera? */
  alcance: CampaignAlcance;
  metaAdId: string;
  metaCampaignId: string;
  /** Gasto ACUMULADO del periodo, como texto. */
  gastoActual: string;
  /** Código ISO de 3 letras (`COP` por defecto). */
  moneda: string;
  activo: boolean;
}

/** Estado del diálogo de «Actualizar el gasto» (un solo campo). */
export interface GastoFormState {
  gastoActual: string;
}

/** Campos que el backend puede reportar en `details.fieldErrors` (para repartir sus mensajes). */
export const CAMPAIGN_FORM_FIELDS = [
  'nombre',
  'metaAdId',
  'metaCampaignId',
  'gastoActual',
  'moneda',
  'activo',
] as const;

/** Códigos ISO que ofrece el formulario (el contrato acepta cualquier trío de letras). */
export const MONEDAS = ['COP', 'USD', 'MXN', 'PEN', 'CLP', 'ARS', 'EUR', 'BRL', 'GTQ', 'PYG'] as const;

/** Moneda por defecto del backend (`campaigns.schemas.ts` → `monedaSchema`). */
export const MONEDA_POR_DEFECTO = 'COP';

/** Opciones del `<select>` de moneda: la de la fila primero si no está entre las habituales. */
export function monedaOptions(actual: string): string[] {
  const code = actual.trim().toUpperCase();
  if (code.length === 0) return [...MONEDAS];

  const conocidas: readonly string[] = MONEDAS;
  return conocidas.includes(code) ? [...MONEDAS] : [code, ...MONEDAS];
}

/** Formulario de una fila nueva: gasto en blanco (el backend lo pone en 0 si no se manda). */
export function emptyCampaignForm(): CampaignFormState {
  return {
    nombre: '',
    alcance: 'anuncio',
    metaAdId: '',
    metaCampaignId: '',
    gastoActual: '',
    moneda: MONEDA_POR_DEFECTO,
    activo: true,
  };
}

/** Fila de la API → estado del formulario. `null` = fila nueva. */
export function campaignFormStateFrom(campaign: Campaign | null): CampaignFormState {
  if (!campaign) return emptyCampaignForm();

  return {
    nombre: campaign.nombre,
    alcance: campaign.metaAdId ? 'anuncio' : 'campana',
    metaAdId: campaign.metaAdId ?? '',
    metaCampaignId: campaign.metaCampaignId ?? '',
    gastoActual: String(campaign.gastoActual),
    moneda: campaign.moneda || MONEDA_POR_DEFECTO,
    activo: campaign.activo,
  };
}

/** Fila de la API → formulario del diálogo de gasto. */
export function gastoFormFrom(campaign: Campaign): GastoFormState {
  return { gastoActual: String(campaign.gastoActual) };
}

/**
 * Monto del input: acepta `25000`, `25000.5` y `25000,5`; `''` o cualquier otra cosa → `null`.
 *
 * No se aceptan separadores de miles («25.000») porque el punto también es el decimal.
 */
export function parseGastoField(value: string): number | null {
  const text = value.trim().replace(',', '.');
  if (text.length === 0) return null;
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return null;

  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Texto → `string[]` depurado (los ids vienen de un input de texto). */
function cleanId(value: string): string {
  return value.trim();
}

/** Los dos ids de Meta tal como deben viajar al backend (sin espacios y con `null` donde toca). */
export function campaignIds(form: CampaignFormState): {
  metaAdId: string | null;
  metaCampaignId: string | null;
} {
  const metaCampaignId = cleanId(form.metaCampaignId);

  // Con alcance «campaña» el `metaAdId` va explícitamente en `null`: es la única forma de que el
  // backend guarde gasto a nivel campaña (`createCampaign` lo exige) y de vaciarlo al editar.
  if (form.alcance === 'campana') {
    return { metaAdId: null, metaCampaignId: metaCampaignId || null };
  }

  return { metaAdId: cleanId(form.metaAdId) || null, metaCampaignId: metaCampaignId || null };
}

/**
 * Formulario → body del `POST`.
 *
 * Solo se manda lo que el admin escribió: sin `gastoActual` el backend guarda 0 y sin `moneda`
 * guarda `COP`. `activo` sí viaja siempre (el formulario lo trae marcado por defecto).
 */
export function createCampaignPayload(form: CampaignFormState): CreateCampaignRequest {
  const ids = campaignIds(form);
  const gasto = parseGastoField(form.gastoActual);
  const moneda = form.moneda.trim().toUpperCase();

  const payload: CreateCampaignRequest = {
    nombre: form.nombre.trim(),
    metaAdId: ids.metaAdId,
    metaCampaignId: ids.metaCampaignId,
    activo: form.activo,
  };

  if (gasto !== null) payload.gastoActual = gasto;
  // La moneda por defecto del backend no viaja: mandarla sería repetir el default.
  if (moneda.length > 0 && moneda !== MONEDA_POR_DEFECTO) payload.moneda = moneda;

  return payload;
}

/**
 * Formulario → body del `PATCH`. El diálogo edita la fila COMPLETA, así que van todos los campos:
 * los ids como `null` cuando quedaron vacíos (para poder vaciarlos de verdad) y el gasto en 0
 * cuando el input está vacío.
 */
export function updateCampaignPayload(form: CampaignFormState): UpdateCampaignRequest {
  const ids = campaignIds(form);
  const moneda = form.moneda.trim().toUpperCase();

  return {
    nombre: form.nombre.trim(),
    metaAdId: ids.metaAdId,
    metaCampaignId: ids.metaCampaignId,
    gastoActual: parseGastoField(form.gastoActual) ?? 0,
    moneda: moneda.length > 0 ? moneda : MONEDA_POR_DEFECTO,
    activo: form.activo,
  };
}

/**
 * `PATCH` de un solo campo, que es la vía que documenta el contrato para mantener el gasto al día
 * mientras no exista el sync con la Meta Ads API (el que abre `GastoDialog` desde la tabla).
 */
export function gastoPayload(form: GastoFormState): UpdateCampaignRequest {
  return { gastoActual: parseGastoField(form.gastoActual) ?? 0 };
}

/** `PATCH` con un solo campo booleano: el switch de la tabla, sin abrir ningún diálogo. */
export function activoPayload(activo: boolean): UpdateCampaignRequest {
  return { activo };
}
